import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, Home, CreditCard, Upload, Banknote, Smartphone, MessageCircle, Copy, Check } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const BUSINESS_PHONE = '85260771493';
const AUTO_OPEN_SECONDS = 5;

const isMobileDevice = () => typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

const OrderConfirmation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [copiedField, setCopiedField] = React.useState<string | null>(null);
  const [whatsappMsg, setWhatsappMsg] = React.useState<string | null>(null);
  const [whatsappLink, setWhatsappLink] = React.useState<string | null>(null);
  const [buttonCooldown, setButtonCooldown] = React.useState(0);
  const [autoOpenAttempted, setAutoOpenAttempted] = React.useState(false);
  const [countdown, setCountdown] = React.useState(AUTO_OPEN_SECONDS);
  const [confirmed, setConfirmed] = React.useState(false);
  const [qrLink, setQrLink] = React.useState<string | null>(null);

  const orderId = searchParams.get('orderId');
  const orderNum = searchParams.get('orderNum') || '';
  const total = searchParams.get('total') || '';

  // Always scroll to the very top when landing on this page
  React.useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  React.useEffect(() => {
    if (buttonCooldown <= 0) return;
    const timer = setInterval(() => {
      setButtonCooldown(c => {
        if (c <= 1) { clearInterval(timer); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [buttonCooldown]);

  // Countdown before automatically opening WhatsApp.
  React.useEffect(() => {
    if (countdown <= 0 || !whatsappLink) return;
    const timer = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) {
          clearInterval(timer);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [countdown, whatsappLink]);

  // Automatically open WhatsApp once the countdown reaches zero.
  React.useEffect(() => {
    if (countdown !== 0 || !whatsappLink || autoOpenAttempted) return;
    setAutoOpenAttempted(true);
    handleOpenWhatsApp(whatsappLink, true);
  }, [countdown, whatsappLink, autoOpenAttempted]);

  // Poll backend to detect when the customer has sent the WhatsApp message.
  React.useEffect(() => {
    if (!orderNum || confirmed) return;
    const check = async () => {
      try {
        const res = await fetch(`/api/public/order-confirmation-status?orderNum=${encodeURIComponent(orderNum)}`);
        const data = await res.json().catch(() => ({}));
        if (data.confirmed) {
          setConfirmed(true);
        }
      } catch (e) {
        // ignore polling errors
      }
    };
    check();
    const timer = setInterval(check, 4000);
    return () => clearInterval(timer);
  }, [orderNum, confirmed]);

  React.useEffect(() => {
    try {
      let msg = sessionStorage.getItem('gs2_last_whatsapp_msg');
      let link = sessionStorage.getItem('gs2_last_whatsapp_link');
      if (!msg && orderNum) {
        msg = `你好，我剛在阿力廚房提交了訂單，會盡快付款，請確認。\n\n訂單號碼：${orderNum}\n總額：HK$${total}`;
      }
      const encodedText = msg ? encodeURIComponent(msg) : '';
      const waMe = `https://wa.me/${BUSINESS_PHONE}?text=${encodedText}`;
      if (!link && msg) {
        link = waMe;
      }
      if (msg) setWhatsappMsg(msg);
      if (link) {
        setWhatsappLink(link);
        setQrLink(waMe);
        setCountdown(AUTO_OPEN_SECONDS);
      }
    } catch { /* ignore */ }
  }, [orderNum, total]);

  const handleBack = () => {
    navigate('/');
  };

  const handleUploadProof = () => {
    navigate(`/payment-proof/${orderNum}`);
  };

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (e) {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  const handleOpenWhatsApp = (link: string, isAuto = false) => {
    // Native app schemes / Android intents must use location.href; a new tab
    // would be blocked by the browser. On mobile, wa.me should also use
    // location.href so the OS can hand it off to the WhatsApp app directly.
    if (link.startsWith('whatsapp://') || link.startsWith('intent://') || (isMobileDevice() && link.startsWith('https://wa.me'))) {
      window.location.href = link;
    } else {
      const win = window.open(link, '_blank');
      if (!win || win.closed) {
        window.location.href = link;
      }
    }
    if (!isAuto) {
      setButtonCooldown(120);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-lg w-full">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 bg-[#25D366]/30 rounded-full blur-lg opacity-60"></div>
              <MessageCircle size={120} className="text-[#25D366] relative z-10" strokeWidth={1.5} />
            </div>
          </div>

          {confirmed ? (
            <>
              <h1 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-3">
                訂單確認完成
              </h1>
              <p className="text-center text-lg md:text-xl text-gray-600 mb-6">
                我們已收到你的 WhatsApp 訊息，下單過程已全部結束。
              </p>
              <div className="bg-green-50 border-2 border-green-200 rounded-xl p-5 mb-6 text-center">
                <div className="flex justify-center mb-3">
                  <CheckCircle size={64} className="text-green-600" />
                </div>
                <p className="text-lg font-bold text-green-800 mb-1">系統已確認你的訂單</p>
                <p className="text-gray-600">訂單編號 #{orderNum}</p>
              </div>
            </>
          ) : (
            <>
              <h1 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-3">
                只差一步：WhatsApp 確認
              </h1>
              <p className="text-center text-lg md:text-xl text-gray-600 mb-6">
                我們已收到你的訂單。WhatsApp 將於 <span className="font-bold text-gray-800">{AUTO_OPEN_SECONDS} 秒內</span> 自動打開，請將確認訊息發送到 <span className="font-bold text-gray-800">6077 1493</span>。
              </p>

              {/* WhatsApp Action */}
              {whatsappMsg && whatsappLink && (
                <div className="bg-[#25D366]/10 border-2 border-[#25D366]/30 rounded-xl p-5 mb-6">
                  {countdown > 0 ? (
                    <p className="text-center text-lg font-bold text-[#128C7E] mb-3">
                      WhatsApp 將於 {countdown} 秒後自動打開
                    </p>
                  ) : (
                    <p className="text-center text-sm text-gray-600 mb-3">
                      如果 WhatsApp 沒有自動打開，請按下面按鈕再次發送。
                    </p>
                  )}
                  {autoOpenAttempted && buttonCooldown === 0 && (
                    <p className="text-center text-xs text-amber-600 mb-3">
                      看來瀏覽器封鎖了自動跳轉，請手動按下面按鈕。
                    </p>
                  )}
                  <button
                    onClick={() => handleOpenWhatsApp(whatsappLink)}
                    disabled={buttonCooldown > 0}
                    className="w-full bg-[#25D366] hover:bg-[#128C7E] disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-lg font-bold py-4 rounded-xl transition-colors flex items-center justify-center gap-2 mb-4"
                  >
                    <MessageCircle size={24} />
                    {buttonCooldown > 0
                      ? `已開啟 WhatsApp（${buttonCooldown} 秒後可再試）`
                      : countdown > 0
                        ? `即將自動打開（${countdown}）`
                        : '開啟 WhatsApp 發送確認'}
                  </button>

                  {!isMobileDevice() && (
                    <div className="flex flex-col items-center gap-2 mb-4">
                      <QRCodeSVG value={qrLink || whatsappLink} size={160} />
                      <span className="text-sm text-gray-600 text-center">
                        電腦沒有 WhatsApp？用手機掃描 QR code 即可打開訊息
                      </span>
                    </div>
                  )}

                  <details className="text-sm text-gray-600">
                    <summary className="cursor-pointer hover:text-gray-800">訊息無法打開？按此複製</summary>
                    <div className="mt-2 bg-white rounded-lg border border-[#25D366]/30 p-3">
                      <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">{whatsappMsg}</pre>
                    </div>
                    <button
                      onClick={() => handleCopy(whatsappMsg, 'whatsappMsg')}
                      className="mt-2 w-full bg-white border-2 border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 font-bold py-2 rounded-xl transition-colors flex items-center justify-center gap-2"
                    >
                      {copiedField === 'whatsappMsg' ? <Check size={18} /> : <Copy size={18} />}
                      {copiedField === 'whatsappMsg' ? '已複製' : '複製 WhatsApp 訊息'}
                    </button>
                  </details>
                </div>
              )}
            </>
          )}

          <div className="bg-white rounded-xl border-2 border-brand-200 p-5 mb-6 shadow">
            <div className="flex justify-between items-center pb-3 border-b border-gray-200 mb-3">
              <span className="text-lg text-gray-600">訂單編號</span>
              <span className="font-bold text-xl text-brand-600">#{orderNum}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-lg text-gray-600">總金額</span>
              <span className="font-bold text-xl text-gray-800">HK${total}</span>
            </div>
          </div>

          {/* Payment Info */}
          <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-6 mb-6">
            <h3 className="text-xl font-bold text-amber-800 mb-4 flex items-center gap-2">
              <CreditCard size={24} />
              請付款至以下賬戶
            </h3>
            <div className="space-y-3 text-lg text-gray-700">
              <div className="flex items-start gap-3">
                <Banknote size={20} className="text-amber-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-bold">HSBC 銀行戶口</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xl font-mono">023-499585-001</p>
                    <button
                      onClick={() => handleCopy('023-499585-001', 'hsbc')}
                      className="p-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 transition-colors"
                      title="複製銀行戶口號碼"
                    >
                      {copiedField === 'hsbc' ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Smartphone size={20} className="text-amber-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-bold">FPS 轉數快 / PayMe</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xl font-mono">93278777</p>
                    <button
                      onClick={() => handleCopy('93278777', 'fps')}
                      className="p-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 transition-colors"
                      title="複製 FPS / PayMe 號碼"
                    >
                      {copiedField === 'fps' ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle size={20} className="text-amber-600 mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-bold">戶口名稱 / 公司名稱</p>
                  <div className="flex items-center gap-2">
                    <p>X-Production Ltd</p>
                    <button
                      onClick={() => handleCopy('X-Production Ltd', 'name')}
                      className="p-1.5 rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-700 transition-colors"
                      title="複製戶口名稱"
                    >
                      {copiedField === 'name' ? <Check size={16} /> : <Copy size={16} />}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Upload Payment Proof */}
          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 mb-6">
            <h3 className="text-xl font-bold text-blue-800 mb-3 flex items-center gap-2">
              <Upload size={24} />
              上傳付款憑證
            </h3>
            <p className="text-lg text-gray-600 mb-4">
              付款後請上傳付款記錄，我們會盡快確認您的訂單。
            </p>
            <button
              onClick={handleUploadProof}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xl font-bold py-4 px-4 rounded-xl transition-all flex items-center justify-center gap-2"
            >
              <Upload size={24} />
              上傳付款記錄
            </button>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-xl p-5 mb-6">
            <div className="text-lg text-gray-700 space-y-3">
              <div className="flex items-start gap-2"><CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" /> 付款後請上傳付款憑證</div>
              <div className="flex items-start gap-2"><CheckCircle size={20} className="text-green-600 mt-0.5 flex-shrink-0" /> 我們確認後會通知您</div>
            </div>
          </div>

          <button
            onClick={handleBack}
            className="w-full bg-brand-500 hover:bg-brand-600 text-white text-xl font-bold py-4 px-4 rounded-xl transition-all flex items-center justify-center gap-2"
          >
            <Home size={24} />
            返回首頁
          </button>
        </div>
      </div>

      <footer className="bg-gray-800 text-gray-400 py-6 text-center">
        <p className="text-xl mb-3">阿力廚房私廚出品，喚醒味蕾</p>
        <div className="flex justify-center gap-4 text-sm">
          <button onClick={() => navigate('/terms-of-service')} className="hover:text-white transition-colors">條款及細則</button>
          <span>|</span>
          <button onClick={() => navigate('/privacy-policy')} className="hover:text-white transition-colors">隱私條款</button>
        </div>
      </footer>
    </div>
  );
};

export default OrderConfirmation;
